import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HousePlug, Blocks, ArrowUpRight, Building2 } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";

function ModuleCard({ type, icon: Icon, title, subtitle, stats, to, index }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-testid={`module-card-${type.toLowerCase()}`}
      onClick={() => navigate(to)}
      className="group relative w-full overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-[0_2px_8px_rgba(16,19,21,0.055)] transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-[#b9a4cc] hover:shadow-[0_10px_26px_rgba(16,19,21,0.085)]"
    >
      <div className="h-1 bg-[#765297]" />
      <div className="flex items-center justify-between border-b border-gray-200 bg-[#faf9f8] px-6 py-5 sm:px-8">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-[#d9cde5] bg-[#f3eff7] text-[#604184] shadow-sm"><Icon size={23} strokeWidth={1.8} /></div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-[#765297]" style={{ letterSpacing: "0.13em" }}>Project Stream {String(index).padStart(2, "0")}</div>
            <h2 className="mt-1 font-heading text-xl font-bold text-gray-950 sm:text-2xl">{title}</h2>
          </div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm transition-[background-color,border-color,color,transform] duration-200 group-hover:translate-x-0.5 group-hover:border-[#9b7ab8] group-hover:bg-[#f3eff7] group-hover:text-[#604184]">
          <ArrowUpRight size={18} strokeWidth={1.8} />
        </div>
      </div>

      <div className="px-6 py-6 sm:px-8 sm:py-7">
        <p className="min-h-10 text-sm leading-relaxed text-gray-500">{subtitle}</p>
        <div className="mt-6 grid grid-cols-2 gap-8 border-t border-gray-200 pt-6">
          <div>
            <div className="bdx-label">Active Projects</div>
            <div data-testid={`${type.toLowerCase()}-active-count`} className="mt-2 font-mono-data text-3xl font-semibold text-gray-950">{stats.active_count}</div>
          </div>
          <div className="border-l border-gray-200 pl-8">
            <div className="bdx-label">Archived / Completed</div>
            <div data-testid={`${type.toLowerCase()}-archived-count`} className="mt-2 font-mono-data text-3xl font-medium text-gray-500">{stats.archived_completed_count}</div>
          </div>
        </div>
        <p className="mt-6 text-[10px] font-medium uppercase text-gray-400" style={{ letterSpacing: "0.06em" }}>Open the register for the stream's financial summary</p>
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div data-testid="dashboard-loading" className="text-sm text-gray-500">Loading dashboard…</div>;

  return (
    <div data-testid="dashboard-page" className="bdx-page-enter">
      <Breadcrumbs items={[{ label: "Dashboard" }]} />
      <div className="flex flex-col gap-5 border-b border-gray-300 pb-7 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.15em" }}><Building2 size={15} strokeWidth={1.8} /> BDX Project Portfolio</div>
          <h1 className="font-heading text-3xl font-bold text-gray-950 sm:text-4xl">Project Streams</h1>
          <p className="mt-2 text-sm text-gray-500">Select the BDX project stream you want to work in.</p>
        </div>
        <div className="text-left sm:text-right">
          <div className="bdx-label">Operational Modules</div>
          <div className="mt-1 font-mono-data text-xl font-semibold text-gray-950">02</div>
        </div>
      </div>

      <div className="bdx-stagger mx-auto mt-8 grid max-w-[1180px] grid-cols-1 gap-6 xl:grid-cols-2">
        <ModuleCard type="SSH" index={1} icon={HousePlug} title="SSH Modules" to="/ssh"
          subtitle="Social Supported Housing — Main House + Granny Flat" stats={data.SSH} />
        <ModuleCard type="MMC" index={2} icon={Blocks} title="MMC Modules" to="/mmc"
          subtitle="Modern Methods of Construction — multi-unit" stats={data.MMC} />
      </div>
    </div>
  );
}